/**
 * Debug logging utility for Nordex frontend.
 * 
 * In production, only warnings and errors are shown.
 * In development, all debug logs are shown.
 * 
 * Industry standard: Keep production console clean while maintaining
 * excellent debugging capabilities in development.
 */

const IS_DEV = import.meta.env.DEV;
const DEBUG_ENABLED = IS_DEV || import.meta.env.VITE_DEBUG === 'true';

/**
 * Styled console logging utility.
 * 
 * Usage:
 * - debug.log('Auth', 'User logged in') - Only in dev
 * - debug.warn('Auth', 'Token expiring soon') - Always shown
 * - debug.error('Auth', 'Login failed', error) - Always shown
 * - debug.info('Build', 'Version 1.0.0') - Always shown (for important info)
 */
export const debug = {
  /**
   * Debug logs - only shown in development.
   * Use for tracking flow, state changes, WebSocket events, etc.
   */
  log: (tag: string, ...args: unknown[]) => {
    if (DEBUG_ENABLED) {
      console.log(`[${tag}]`, ...args);
    }
  },

  /**
   * Warnings - always shown.
   * Use for non-critical issues that should be investigated.
   */
  warn: (tag: string, ...args: unknown[]) => {
    console.warn(`[${tag}]`, ...args);
  },

  /**
   * Errors - always shown.
   * Use for actual errors that need immediate attention.
   */
  error: (tag: string, ...args: unknown[]) => {
    console.error(`[${tag}]`, ...args);
  },

  /**
   * Info logs - always shown.
   * Use for important information like build version, environment, etc.
   */
  info: (tag: string, ...args: unknown[]) => {
    console.log(`[${tag}]`, ...args);
  },

  /**
   * Styled info block - always shown.
   * Use for important banners like build info.
   */
  banner: (title: string, style: string, ...messages: string[]) => {
    console.log(`%c${title}`, style);
    messages.forEach(msg => console.log(msg));
  },
};

/**
 * Check if debug mode is enabled.
 */
export const isDebugEnabled = () => DEBUG_ENABLED;

